import { useState, useRef, useEffect } from "react";
import { chatAPI } from "../utils/api";
import MarkdownRenderer from "../components/MarkdownRenderer";

const QUICK_QUESTIONS = [
  { icon: "💰", text: "How much will it cost to build a 1000 sqft house in Nagpur?" },
  { icon: "🏛️", text: "What are the recommended sizes for column, beam, and footing for a G+1 house (IS 456)?" },
  { icon: "🌿", text: "What are the best directions for Kitchen, Master Bedroom, and Main Gate according to Vastu?" },
  { icon: "📋", text: "What documents are required for municipal building permission and approval (BPAMS/BBMP)?" },
  { icon: "⚡", text: "What are the electrical load, wiring gauges, and plumbing pipe standards for a 3BHK house?" },
  { icon: "🛡️", text: "What are the best materials and methods for roof waterproofing and seepage protection?" },
];

const INTENT_LABELS = {
  cost: "💰 Cost & Material Analysis",
  structural: "🏛️ Structural Design & IS Codes",
  planning: "📐 Planning & Approvals",
  MEP: "⚡ MEP & Services",
  finishing: "🎨 Finishing & Interiors",
  loan: "🏦 Loan & EMI Planning",
  contract: "📑 Contract Models",
  defect: "🛠️ Defect Diagnosis",
  vastu: "🌿 Vastu Shastra",
  floor_plan: "📐 Floor Plan & Room Sizes",
  market_prices: "🏷️ Brand & Market Prices",
  green_building: "🌱 Green Building",
};

const TOOL_LABELS = {
  calculate_boq: "BOQ & cost calculator",
  calculate_loan_schedule: "Loan & EMI calculator",
  compare_contract_models: "Contract comparison",
  diagnose_construction_defect: "Defect diagnosis engine",
  evaluate_vastu: "Vastu evaluator",
  generate_floor_plan_layout: "Floor plan calculator",
  get_brand_market_prices: "Brand price index",
};

const FALLBACK_REASON_TEXT = {
  gemini_not_configured: "Gemini is not configured on the server",
  gemini_sdk_unavailable: "The Gemini SDK is not installed on the server",
  gemini_auth_failed: "Gemini rejected the server's API key",
  gemini_model_unavailable: "The configured Gemini model is unavailable",
  gemini_rate_limited: "Gemini rate limit reached",
  gemini_server_error: "Gemini service error",
  gemini_timeout: "Gemini did not respond in time",
  gemini_network_error: "Could not reach Gemini",
  gemini_empty_response: "Gemini returned an empty answer",
  gemini_request_rejected: "Gemini rejected the request",
};

const FOLLOW_UP_SUGGESTIONS = [
  "💰 Provide a detailed material & labor cost table for this",
  "🏛️ What are the foundation and structural safety guidelines?",
  "🌿 How to apply Vastu principles non-destructively?",
  "⏳ Create a month-by-month construction timeline diagram",
  "💡 Give top 5 actionable tips to save cost with contractors",
];

export default function ChatPage({ sessionId, initialPrompt }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Handle initial incoming prompt from other pages (e.g. from Vastu / BOQ)
  useEffect(() => {
    if (initialPrompt) {
      handleSend(initialPrompt);
    }
  }, [initialPrompt]);

  const handleSend = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const data = await chatAPI.sendMessage(msg, sessionId);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          sources: data.sources,
          intent: data.intent,
          modelUsed: data.model_used,
          aiMode: data.ai_mode,
          fallbackReason: data.fallback_reason,
          toolsUsed: data.tools_used || [],
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `⚠️ Error occurred: ${err.message}. Please try again shortly.`,
          sources: [],
          intent: "error",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleSpeak = (text, index) => {
    if (!("speechSynthesis" in window)) {
      alert("Text-to-speech is not supported in this browser.");
      return;
    }

    if (speakingIndex === index) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(null);
      return;
    }

    window.speechSynthesis.cancel();
    // Clean text of markdown artifacts for natural speaking
    const cleanText = text
      .replace(/[*#_`>]/g, "")
      .replace(/\[.*?\]\(.*?\)/g, "")
      .replace(/!\[.*?\]\(.*?\)/g, "")
      .slice(0, 800);

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.95;
    utterance.onend = () => setSpeakingIndex(null);
    utterance.onerror = () => setSpeakingIndex(null);

    setSpeakingIndex(index);
    window.speechSynthesis.speak(utterance);
  };

  const handleReset = async () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    await chatAPI.resetSession(sessionId).catch(() => {});
    setMessages([]);
  };

  return (
    <div className="chat-page">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="header-status-indicator">
            <span className="live-dot" />
            <span className="status-label">NirmanAI Pro Assistant • Live</span>
          </div>
          <h1>Construction AI Intelligence 🏗️</h1>
          <div className="subtitle">
            Certified IS-code &amp; Vastu-compliant planning, cost analysis, and diagrams in English
          </div>
        </div>
        {messages.length > 0 && (
          <div className="header-actions">
            <button onClick={() => window.print()} className="action-pill-btn" title="Print chat">
              🖨️ Print / Save
            </button>
            <button onClick={handleReset} className="action-pill-btn reset-btn">
              🔄 New Conversation
            </button>
          </div>
        )}
      </div>

      <div className="messages-area">
        {messages.length === 0 ? (
          <div className="welcome-state">
            <div className="welcome-icon">🏗️</div>
            <h2>Welcome to NirmanAI Assistant!</h2>
            <p>
              Ask any question about your house or commercial construction project. You will receive reliable advice with <strong>transparent cost tables</strong>,{" "}
              <strong>IS code guidelines</strong>, and <strong>visual diagrams</strong>.
            </p>
            <div className="quick-questions">
              {QUICK_QUESTIONS.map((q, i) => (
                <button key={i} className="quick-btn" onClick={() => handleSend(q.text)}>
                  <span className="q-icon">{q.icon}</span>
                  <span className="q-text">{q.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div key={i} className={`message ${msg.role}`}>
                <div className="msg-avatar">{msg.role === "user" ? "👤" : "🏗️"}</div>
                <div className="msg-wrapper">
                  {msg.role === "assistant" && (
                    <div className="assistant-meta-bar">
                      <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                        <span className="intent-badge">
                          {INTENT_LABELS[msg.intent] || "📋 NirmanAI Expert Advice"}
                        </span>
                        {msg.intent !== "error" &&
                          (msg.aiMode === "gemini" ? (
                            <span className="ai-mode-badge gemini" title={`Generated by Google Gemini (${msg.modelUsed})`}>
                              ✦ Gemini · {msg.modelUsed}
                            </span>
                          ) : (
                            <span
                              className="ai-mode-badge offline"
                              title={`Offline mode: this answer was produced by NirmanAI's deterministic engine, not by an AI model. ${
                                FALLBACK_REASON_TEXT[msg.fallbackReason] || "Gemini was unavailable"
                              }.`}
                            >
                              ⚙ Offline Engine · not AI-generated
                            </span>
                          ))}
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          onClick={() => handleSpeak(msg.content, i)}
                          className="copy-btn"
                          title="Listen to audio"
                        >
                          {speakingIndex === i ? "⏹️ Stop" : "🔊 Listen to Audio"}
                        </button>
                        <button
                          onClick={() => handleCopy(msg.content, i)}
                          className="copy-btn"
                          title="Copy answer"
                        >
                          {copiedIndex === i ? "✓ Copied!" : "📋 Copy"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="msg-bubble">
                    {msg.role === "assistant" ? (
                      <MarkdownRenderer content={msg.content} onQuickAction={handleSend} />
                    ) : (
                      <div className="user-text">{msg.content}</div>
                    )}
                  </div>

                  {msg.role === "assistant" && msg.toolsUsed?.some((t) => t.status === "success") && (
                    <div className="msg-sources">
                      <span className="sources-label">🧮 Calculated by:</span>
                      {msg.toolsUsed
                        .filter((t) => t.status === "success")
                        .map((t, j) => (
                          <span key={j} className="source-tag" title={(t.assumptions || []).join("\n") || undefined}>
                            {TOOL_LABELS[t.tool] || t.tool}
                            {t.headline ? ` — ${t.headline}` : ""}
                          </span>
                        ))}
                    </div>
                  )}

                  {msg.role === "assistant" && msg.sources?.length > 0 && (
                    <div className="msg-sources">
                      <span className="sources-label">
                        {msg.sources[0].usage === "shown_in_offline_answer"
                          ? "📚 Knowledge base excerpts shown:"
                          : "📚 Knowledge base sources cited:"}
                      </span>
                      {msg.sources.map((s, j) => (
                        <span key={j} className="source-tag">
                          {s.ref ? `[${s.ref}] ` : ""}
                          {s.title}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="message assistant typing-indicator">
                <div className="msg-avatar">🏗️</div>
                <div className="msg-bubble">
                  <div className="loading-container">
                    <div className="typing-dot" />
                    <div className="typing-dot" />
                    <div className="typing-dot" />
                    <span className="generating-text">Analyzing construction data...</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {messages.length > 0 && !loading && (
        <div className="followup-suggestions-bar">
          <span className="followup-label">💡 Ask follow-up:</span>
          <div className="followup-chips">
            {FOLLOW_UP_SUGGESTIONS.map((s, idx) => (
              <button key={idx} className="followup-chip" onClick={() => handleSend(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-input-area">
        <div className="input-row">
          <textarea
            ref={textareaRef}
            className="chat-textarea"
            placeholder="Ask about construction, cost estimates, Vastu, or structure... (Shift+Enter for new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
            }}
          />
          <button
            className="send-btn"
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            title="Send query"
          >
            {loading ? <div className="spinner" /> : "➤"}
          </button>
        </div>
      </div>
    </div>
  );
}
